import React, { useState, useEffect, useMemo, Fragment } from "react";
import { useLocation } from "react-router-dom";
import { message, Col, Row } from "antd";
import PhoneInput from "react-phone-number-input";
import {
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from "libphonenumber-js";
import jwtDecode from "jwt-decode";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/UseLanguage";
import countriesData from "./countries.json";
import { submitBasicInfo } from "../../services/AccountDashboardAPIs";
import "./UserIntro.scss";
import "react-phone-number-input/style.css";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css"; 
import { getIndustryLabel, industryOptions } from "../../utils/industryOptions";
import StyledDropdown from "../../components/reusable/StyledDropdown";
import StyledTextInput from "../../components/reusable/StyledTextInput";

interface UserIntroProps {
  onNext?: (_isValid: boolean, _company?: string) => void;
}

interface Country {
  code: string;
  label: string;
  phone: string;
  phoneLength: number | number[];
}

interface CountryInfo {
  countryName: string;
  countryCode: string;
  phoneLength: number;
}

const UserIntro: React.FC<UserIntroProps> = ({ onNext }) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get("token");
  const { t } = useTranslation();
  useLanguage(); // Initialize RTL support

  interface DecodedToken {
    user_id: string;
    tenant_id: string;
    role_id: string;
    api_key: string;
  }
useEffect(() => {
  if (token) {
    try {
      const decodedToken: DecodedToken = jwtDecode(token);
      localStorage.setItem("user_id", decodedToken.user_id);
      localStorage.setItem("tenant_id", decodedToken.tenant_id);
      localStorage.setItem("role_id", decodedToken.role_id);
      localStorage.setItem("api_key", decodedToken.api_key);

      const cleanUrl = `${window.location.origin}${window.location.pathname}`;
      window.history.replaceState({}, document.title, cleanUrl);
    } catch {}
  }
}, [token]);

  const api_key = localStorage.getItem("api_key")?.trim();
  const user_id = localStorage.getItem("user_id");
  const tenant_id = localStorage.getItem("tenant_id");
const [companyUrl, setCompanyUrl] = useState("");
const [urlError, setUrlError] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [company, setCompany] = useState("");
  const [profileImage, setProfileImage] = useState<File | null>(null);
const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [value, setValue] = useState<string | undefined>("");

  const [selectedCountry, setSelectedCountry] = useState("PK");
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [companyError, setCompanyError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [industry, setIndustry] = useState("");
  const [industryError, setIndustryError] = useState("");

  const countryMap: Record<string, CountryInfo> = useMemo(() => {
    return (countriesData as Country[]).reduce(
      (map: Record<string, CountryInfo>, country: Country) => {
        const length = Array.isArray(country.phoneLength)
          ? country.phoneLength[0]
          : country.phoneLength;

        map[country.code] = {
          countryName: country.label,
          countryCode: country.phone,
          phoneLength: length,
        };
        return map;
      },
      {}
    );
  }, []);

  useEffect(() => {
    document.body.style.overflow = "hidden";
  }, []);

useEffect(() => {
  setFirstName(localStorage.getItem("firstName") || "");
  setLastName(localStorage.getItem("lastName") || "");
  setCompany(localStorage.getItem("company") || "");
  setCompanyUrl(localStorage.getItem("companyUrl") || "");
  setValue(localStorage.getItem("phoneNumber") || "");
  setPreviewImage(localStorage.getItem("previewImage") || null);
  setIndustry(localStorage.getItem("industry") || "");
}, []);

const handleFirstName = (v: string) => {
  setFirstName(v);
  localStorage.setItem("firstName", v);
};
const handleLastName = (v: string) => {
  setLastName(v);
  localStorage.setItem("lastName", v);
};
const handleCompany = (v: string) => {
  setCompany(v);
  localStorage.setItem("company", v);
};

const handleIndustry = (v: string) => {
  setIndustry(v);
  localStorage.setItem("industry", v);
};


  const resizeImageToSquare = (file: File, size = 200): Promise<File> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {return reject("Canvas not supported");}

      canvas.width = size;
      canvas.height = size;
      ctx.fillStyle = "#fff";
      ctx.fillRect(0, 0, size, size);
      const scale = Math.min(size / img.width, size / img.height);
      const newWidth = img.width * scale;
      const newHeight = img.height * scale;
      const dx = (size - newWidth) / 2;
      const dy = (size - newHeight) / 2;
      ctx.drawImage(img, dx, dy, newWidth, newHeight);

      canvas.toBlob(
        blob => {
          if (!blob) {return reject("Failed to create blob");}
          const resizedFile = new File([blob], file.name, { type: file.type });
          resolve(resizedFile);
        },
        file.type || "image/png",
        1.0
      );
    };

    img.onerror = () => reject("Image load failed");
  });
};

const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) {return;}

  if (previewImage) {URL.revokeObjectURL(previewImage);}

  try {
    const resizedFile = await resizeImageToSquare(file, 200);
    const reader = new FileReader();
reader.onloadend = () => {
  const base64 = reader.result as string;
  setPreviewImage(base64);
  localStorage.setItem("previewImage", base64);
};
reader.readAsDataURL(resizedFile);
    setProfileImage(resizedFile);
  } catch (_error) {
    message.error(t("userIntro.errors.imageResizeFailed"));
  }
};

  const handlePhoneNumberChange = (newValue: string | undefined) => {
    setValue(newValue);
    localStorage.setItem("phoneNumber", newValue || "");
    setPhoneError("");

    if (newValue) {
      const phoneNumber = parsePhoneNumberFromString(newValue);
      if (phoneNumber?.country) {
        setSelectedCountry(phoneNumber.country);
      } else {
        setSelectedCountry("");
      }
    }
  };

  const phoneCountryFormat = useMemo(() => {
    const country = countryMap[selectedCountry];
    return country
      ? `+${country.countryCode} ${".".repeat(country.phoneLength)}`
      : undefined;
  }, [selectedCountry, countryMap]);

  const placeholder = useMemo(() => {
    if (!phoneCountryFormat) {
      return t("userIntro.phonePlaceholder");
    }

    let example = phoneCountryFormat.replace(/\./g, "X");
    if (selectedCountry === "FR") {
      example = example.replace("X", "6");
    }

    while (example.includes("X")) {
      example = example.replace("X", `${Math.floor(Math.random() * 9 + 1)}`);
    }

    return example;
  }, [phoneCountryFormat, selectedCountry, t]);

  const handleValidation = async () => {
    setFirstNameError("");
    setLastNameError("");
    setCompanyError("");
    setPhoneError("");
    setUrlError("");
    setIndustryError("");

    let valid = true;

    if (!firstName.trim()) {
      setFirstNameError(t("userIntro.errors.firstNameRequired"));
      valid = false;
    } else if (firstName.length < 3) {
      setFirstNameError(t("userIntro.errors.firstNameMin"));
      valid = false;
    }

    if (!lastName.trim()) {
      setLastNameError(t("userIntro.errors.lastNameRequired"));
      valid = false;
    } else if (lastName.length < 3) {
      setLastNameError(t("userIntro.errors.lastNameMin"));
      valid = false;
    }

    if (!company.trim()) {
      setCompanyError(t("userIntro.errors.companyRequired"));
      valid = false;
    } else if (company.length > 100) {
      setCompanyError(t("userIntro.errors.companyMax"));
      valid = false;
    } else if (/^[^a-zA-Z]/.test(company)) {
      setCompanyError(t("userIntro.errors.companyStartsWithLetter"));
      valid = false;
    }

    if (!value?.trim()) {
      setPhoneError(t("userIntro.errors.phoneRequired"));
      valid = false;
    } else {
      const country = countryMap[selectedCountry];
      if (country) {
        const stripped = value.replace(`+${country.countryCode}`, "");
        if (stripped.length !== country.phoneLength) {
          setPhoneError(
            t("userIntro.errors.phoneDigits", {
              digits: country.phoneLength,
              country: country.countryName,
            })
          );
          valid = false;
        } else if (!isValidPhoneNumber(value)) {
          setPhoneError(t("userIntro.errors.phoneInvalid"));
          valid = false;
        }
      }
    }

      if (!companyUrl?.trim()) {
    setUrlError(t("userIntro.errors.urlRequired"));
    valid = false;
  } else {
    try {
      new URL(companyUrl); 
    } catch {
      setUrlError(t("userIntro.errors.urlInvalid"));
      valid = false;
    }
  }

    if (!industry.trim()) {
      setIndustryError(t("userIntro.errors.industryRequired"));
      valid = false;
    }
    if (!api_key) {
      message.error(t("userIntro.errors.missingSession"));
      return;
    }

    if (valid) {
      localStorage.setItem("company_name", company.trim());
const { ok, data } = await submitBasicInfo({
  api_key,
  tenant_id,
  user_id,
  tenant_name: company,
  first_name: firstName,
  last_name: lastName,
  phone_number: value,
  website_url: companyUrl,
  industry,
  profile_image: profileImage,
});

      if (ok) {
        onNext?.(true, company);
      } else {
        message.error(data.message || t("userIntro.errors.submitFailed"));
      }
    }
  };

  // URL validation regex (covers https/http)
const validateUrl = (url: string) => {
  const pattern = /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)([\/\w .-]*)*\/?$/;
  return pattern.test(url);
};
  const handleUrlChange = (value: string) => {
  setCompanyUrl(value);
localStorage.setItem("companyUrl", value || "");
  if (!value) {
    setUrlError(t("userIntro.errors.urlRequired"));
  } else if (!validateUrl(value)) {
    setUrlError(t("userIntro.errors.urlExample"));
  } else {
    setUrlError("");
  }
};


  return (
    <Fragment>
      <div className="container-wrap">
        <h2 className="h2design">{t("userIntro.title")}</h2>
       <p className="Info-text">
  {t("userIntro.uploadBrandLogo")} <span className="required-star">*</span>
</p>
     <div className="upload-container">
  <Tippy content={t("userIntro.pngOnly")} placement="top">
    <label htmlFor="fileUpload" className="upload-circle">
      {previewImage ? (
        <img src={previewImage} alt="Preview" />
      ) : (
        <span>
          {t("userIntro.upload")}
          <br />
          {t("userIntro.yourLogo")}
        </span>
      )}
    </label>
  </Tippy>

  <input
    type="file"
    accept="image/png"
    id="fileUpload"
    style={{ display: "none" }}
    onChange={handleImageUpload}
  />
</div>

        <Row gutter={0} className="user-intro-row">
          <Col xs={16}>
            <div className="form-section">
              <div className="input-container">
                <label className="intro-label">
                  {t("userIntro.firstName")}
                  <span className="required-star">*</span>
                  <StyledTextInput
                    required
                    value={firstName}
                    className="input-style"
                    onChange={e => handleFirstName(e.target.value)}
                  />
                  <div className="error-message">{firstNameError}</div>
                </label>
              </div>
              <div className="input-container">
                <label className="intro-label">
                  {t("userIntro.lastName")}
                  <span className="required-star">*</span>
                  <StyledTextInput
                    required
                    value={lastName}
                    className="input-style"
                    onChange={e => handleLastName(e.target.value)}
                  />
                  <div className="error-message">{lastNameError}</div>
                </label>
              </div>
              <div className="input-container">
                <label className="intro-label">
                  {t("userIntro.company")}
                  <span className="required-star">*</span>
                  <StyledTextInput
                    required
                    value={company}
                    className="input-style"
                    onChange={e => handleCompany(e.target.value)}
                  />
                  <div className="error-message">{companyError}</div>
                </label>
              </div>
              <div className="input-container">
  <label className="intro-label">
    {t("userIntro.websiteUrl")}
    <span className="required-star">*</span>
    <StyledTextInput
      type="url"
      value={companyUrl}
      className="input-style"
      onChange={e => handleUrlChange(e.target.value)}
      required
    />
    <div className="error-message">{urlError}</div>
  </label>
</div>
              <div className="input-container">
                <label className="intro-label">
                  {t("userIntro.industry")}
                  <span className="required-star">*</span>
                  <StyledDropdown
                    value={industry || undefined}
                    className="industry-dropdown"
                    placeholder={t("userIntro.selectIndustry")}
                    onChange={value => handleIndustry(value as string)}
                    options={industryOptions.map(option => ({
                      label: getIndustryLabel(option, t),
                      value: option,
                    }))}
                  />
                  <div className="error-message">{industryError}</div>
                </label>
              </div>
              <div className="input-container">
                <label className="intro-label">
                  {t("userIntro.contactNumber")}
                  <span className="required-star">*</span>
                  <div className="phone-input-wrapper">
                    <PhoneInput
                      international
                      defaultCountry="PK"
                      value={value}
                      onChange={handlePhoneNumberChange}
                      placeholder={placeholder}
                      className="phone-input"
                      inputClassName="phone-input__input"
                    />
                  </div>
                  <div className="error-message">{phoneError}</div>
                </label>
              </div>
            </div>
          </Col>
          <Col xs={8}>
            <div className="image-section">
              <img src="/images/Girl.png" alt="Girl" className="girl" />
            </div>
          </Col>
        </Row>
        <button
          id="nextBtn"
          onClick={handleValidation}
          style={{ display: "none" }}
        >
          {t("userIntro.next")}
        </button>
      </div>
    </Fragment>
  );
};

export default UserIntro;
